import { useFetcher, useOutletContext } from "@remix-run/react";
import { ShirtspaceResponseMessage } from "@services/shirtspace.types";
import get from "lodash/get";
import { useEffect, useState } from "react";
import FlashMessage from "~/components/FlashMessage";
import { Close } from "~/components/Modal";
import { AccountSurveyActionResponse } from "~/entities/actions/types";
import { StoreLoaderContext } from "~/entities/global/types";
import {
  BUY_FREQUENCY_QUESTION_ID,
  LICENSE_QUESTION_ID,
  LICENSE_YES_ANSWER_ID,
  PERMIT_QUESTION_ID,
  WORK_FREQUENCY_QUESTION_ID,
} from "~/entities/survey/constants";
import RadioField from "./RadioField";
import SurveyQuestion from "./SurveyQuestion";

const ErrorMessage: React.FC<{
  error?: ShirtspaceResponseMessage;
}> = ({ error }) => {
  if (!error) return null;
  return (
    <div className="mt-[-1rem]">
      <FlashMessage
        kind="failure"
        message={error.text}
      />
    </div>
  );
};

const WorkFrequencyQuestion: React.FC = () => (
  <SurveyQuestion
    ordinal={1}
    question="How often do you work with blank apparel?"
  >
    <RadioField
      id="full_time"
      name={WORK_FREQUENCY_QUESTION_ID}
      value="460"
    >
      Full-time
    </RadioField>
    <RadioField
      id="part_time"
      name={WORK_FREQUENCY_QUESTION_ID}
      value="461"
    >
      Part-time
    </RadioField>
    <RadioField
      id="occasionally"
      name={WORK_FREQUENCY_QUESTION_ID}
      value="462"
    >
      Occasionally or as a hobby
    </RadioField>
  </SurveyQuestion>
);

const BuyFrequencyQuestion: React.FC = () => (
  <SurveyQuestion
    ordinal={2}
    question="How often do you buy apparel?"
  >
    <RadioField
      id="weekly"
      name={BUY_FREQUENCY_QUESTION_ID}
      value="463"
    >
      Weekly
    </RadioField>
    <RadioField
      id="monthly"
      name={BUY_FREQUENCY_QUESTION_ID}
      value="464"
    >
      Monthly
    </RadioField>
    <RadioField
      id="semi_annually"
      name={BUY_FREQUENCY_QUESTION_ID}
      value="465"
    >
      A couple times a year
    </RadioField>
  </SurveyQuestion>
);

const LicenseQuestion: React.FC<{
  checkedValue?: string;
  onChange: (value: string) => void;
}> = ({ checkedValue, onChange }) => (
  <SurveyQuestion
    ordinal={3}
    question="Do you have a business license?"
  >
    <RadioField
      id="license_yes"
      name={LICENSE_QUESTION_ID}
      value={LICENSE_YES_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Yes
    </RadioField>
    <RadioField
      id="license_no"
      name={LICENSE_QUESTION_ID}
      value="467"
      checkedValue={checkedValue}
      onChange={onChange}
    >
      No
    </RadioField>
    <RadioField
      id="license_in_process"
      name={LICENSE_QUESTION_ID}
      value="468"
      checkedValue={checkedValue}
      onChange={onChange}
    >
      In the process of getting one
    </RadioField>
  </SurveyQuestion>
);

const PermitQuestion: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  if (!isActive) return null;
  return (
    <SurveyQuestion
      ordinal={4}
      question="Do you have a reseller's permit?"
    >
      <RadioField
        id="permit_yes"
        name={PERMIT_QUESTION_ID}
        value="469"
      >
        Yes
      </RadioField>
      <RadioField
        id="permit_no"
        name={PERMIT_QUESTION_ID}
        value="470"
      >
        No
      </RadioField>
      <RadioField
        id="permit_not_sure"
        name={PERMIT_QUESTION_ID}
        value="471"
      >
        Not sure
      </RadioField>
    </SurveyQuestion>
  );
};

type Props = {
  onSuccess: () => void;
  isActive: boolean;
};
const BusinessSurvey: React.FC<Props> = ({ onSuccess, isActive }) => {
  const [errors, setErrors] = useState<ShirtspaceResponseMessage[]>([]);
  const [license, setLicense] = useState<string | undefined>(undefined);

  const context = useOutletContext<StoreLoaderContext>();
  const fetcher = useFetcher<AccountSurveyActionResponse>();

  useEffect(() => {
    if (get(fetcher, "data.ok") === true) {
      setErrors([]);
      onSuccess();
    } else {
      setErrors(get(fetcher, "data.messages", []));
    }
  }, [fetcher.data]);

  if (!isActive) return null;

  const handleClose = () => {
    const finishRegistration = confirm(
      "To get your special member pricing, complete your registration.\nPress OK to complete your registration.",
    );
    if (!finishRegistration) {
      // @todo handle signed-in refresh
      context.setCreateAccountWizardOpen(false);
    }
  };

  return (
    <>
      <Close onClick={handleClose} />
      <fetcher.Form
        method="post"
        action="/resources/create-account-wizard"
        className="flex flex-col gap-7 xsm:gap-4 md:gap-3.5 px-5"
        data-testid="business-survey-form"
      >
        <input
          type="hidden"
          name="intent"
          value="business_survey"
        />
        <h2 className="text-[20px] text-blue900 font-sans mt-0 uppercase font-bold">
          Let&apos;s set up wholesale pricing.
        </h2>

        <ErrorMessage error={errors.find((error) => error.type === "error")} />

        <WorkFrequencyQuestion />
        <BuyFrequencyQuestion />
        <LicenseQuestion
          checkedValue={license}
          onChange={(val) => setLicense(val)}
        />
        <PermitQuestion isActive={license === LICENSE_YES_ANSWER_ID} />

        <button
          type="submit"
          className="h-8 w-auto self-start rounded-md bg-blue hover:bg-blue200 disabled:bg-blue500 px-4 text-lg uppercase text-white mb-[-10px]"
          disabled={fetcher.state === "submitting"}
        >
          {fetcher.state === "submitting" ? "Submitting..." : "Almost done"}
        </button>
      </fetcher.Form>
    </>
  );
};

export default BusinessSurvey;
